/**
 * Panel multi-cobro del organizador.
 *
 * Magic link 60 min. Sin login persistente.
 * Rediseño: tarjetas editoriales con vertical (mono), título Spectral, badge con
 * dot y barra de progreso del color del estado.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import { copyFor, statusLabel, statusDescription } from '../utils/vertical-copy';
import TopBar from '../components/TopBar';


interface CobroItem {
  slug: string;
  admin_token: string;
  type: string;
  title: string;
  status: string;
  amount_per_share?: number;
  expected_shares?: number;
  paid_shares_count: number;
  total_paid_amount: number;
  created_at: string;
  completed_at?: string;
  settled_at?: string;
  admin_url: string;
  public_url: string;
}


interface DashboardTotals {
  cobros: number;
  by_status: Record<string, number>;
  recaudado_total_eur: number;
  recaudado_mes_eur: number;
  pendiente_cobrar_eur: number;
}

interface DeadlineSoon {
  slug: string;
  title: string;
  deadline: string | null;
  paid_shares_count: number;
  expected_shares: number | null;
  admin_url: string;
}

interface PendingSettlement {
  slug: string;
  title: string;
  total_paid_amount: number;
  settlement_status: string;
  admin_url: string;
}

interface DashboardData {
  totals: DashboardTotals;
  deadlines_soon: DeadlineSoon[];
  pending_settlements: PendingSettlement[];
}


const STATUS_STYLE: Record<string, { dot: string; text: string; bg: string }> = {
  draft:     { dot: '#A89D8E', text: '#6E6557', bg: '#F0EBE2' },
  open:      { dot: '#5E7B3E', text: '#46602C', bg: '#E8EEDC' },
  completed: { dot: '#1C5D82', text: '#154B6B', bg: '#E6EEF3' },
  expired:   { dot: '#BE841B', text: '#8A5F12', bg: '#F7EBD2' },
  cancelled: { dot: '#B0392C', text: '#8A2C22', bg: '#F6E0DC' },
  settled:   { dot: '#15506B', text: '#103B50', bg: '#E2EBF0' },
};

function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function MyCobrosPage() {
  const [params] = useSearchParams();
  const token = params.get('t');

  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [list, setList] = useState<CobroItem[] | null>(null);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadEmail, setLoadEmail] = useState('');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      api.get(`/public/group-buys/by-token/${encodeURIComponent(token)}`),
      api.get(`/public/group-buys/by-token/${encodeURIComponent(token)}/dashboard`),
    ])
      .then(([listRes, dashRes]) => {
        setList(listRes.data.items);
        setLoadEmail(listRes.data.email);
        setDashboard({
          totals: dashRes.data.totals,
          deadlines_soon: dashRes.data.deadlines_soon || [],
          pending_settlements: dashRes.data.pending_settlements || [],
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Token inválido o caducado');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    setSending(true);
    try {
      await api.post('/public/group-buys/lookup', { email });
      setSent(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  // 1) Pedir magic link
  if (!token) {
    return (
      <div className="min-h-screen bg-surface text-ink font-sans antialiased">
        <TopBar contextLabel="Mis cobros" rightCta={{ label: "+ Crear", to: "/nuevo" }} />
        <main className="max-w-md mx-auto px-6 pt-16 md:pt-24 pb-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Tus cobros
          </div>
          <h1 className="font-display font-bold text-[36px] leading-[1.04] tracking-[-0.02em] text-ink mb-3">
            Entra sin contraseña.
          </h1>
          <p className="text-[14.5px] text-ink-700 mb-7">
            Pon el email con el que creaste tus cobros y te mandamos el acceso.
            El enlace <strong className="text-ink">caduca en 60 minutos</strong>.
          </p>
          {sent ? (
            <div className="bg-olive-50 border border-[#C7D5A8] rounded-card p-5 text-[14px] text-olive-700">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] mb-2">
                · Listo
              </div>
              Te lo hemos mandado a <strong>{email}</strong>.
              <p className="text-[12.5px] text-ink-500 mt-2">
                Caduca en 60 minutos · Si no llega, revisa spam.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="tu@email.com"
                className="cc-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed"
              >
                {sending ? 'Un momento…' : 'Mandarme el enlace'}
              </button>
            </form>
          )}
        </main>
        <Style />
      </div>
    );
  }

  if (loading)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center font-sans text-ink-500">
        Un momento…
      </div>
    );

  if (!list)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center font-sans">
        <div className="max-w-sm">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Enlace caducado
          </div>
          <h1 className="font-display font-bold text-[28px] text-ink mb-2 tracking-[-0.02em]">
            Este enlace ya no vale
          </h1>
          <p className="text-ink-700 text-[14.5px] mb-4">
            Pide uno nuevo desde Mis cobros.
          </p>
          <Link
            to="/mis-cobros"
            className="inline-block px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn"
          >
            Pedir otro enlace
          </Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Mis cobros" rightCta={{ label: "+ Crear", to: "/nuevo" }} />
      <header className="bg-paper border-b border-line">
        <div className="max-w-5xl mx-auto px-6 py-9">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · {loadEmail}
          </div>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h1 className="font-display font-bold text-[36px] md:text-[44px] leading-[1.02] tracking-[-0.02em] text-ink">
              Tus cobros
            </h1>
            <Link
              to="/nuevo"
              className="px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition"
            >
              + Crear otro
            </Link>
          </div>
          <p className="text-[13.5px] text-ink-500 mt-3">
            {list.length === 0
              ? 'Aún no hay ninguno.'
              : list.length === 1
              ? '1 cobro'
              : `${list.length} cobros`}
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {dashboard && list.length > 1 && (
          <DashboardSummary data={dashboard} />
        )}
        {list.length === 0 ? (
          <div className="border-[1.5px] border-dashed border-line-strong rounded-card p-10 text-center bg-paper">
            <div
              className="mx-auto mb-4 w-12 h-12 rounded-xl"
              style={{ background: '#F4EEE3', border: '1.5px solid #E5DCCD' }}
              aria-hidden
            />
            <h2 className="font-display font-semibold text-[22px] text-ink tracking-[-0.01em] mb-2">
              Aún no tienes ningún cobro.
            </h2>
            <p className="text-[14px] text-ink-700 mb-6">Tarda 30 segundos crearlo.</p>
            <Link
              to="/nuevo"
              className="inline-block px-6 py-3 rounded-xl bg-brand text-paper font-semibold text-[14.5px] shadow-btn hover:bg-brand-700 transition"
            >
              Crear mi primer cobro →
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {list.map((c) => (
              <CobroCard key={c.slug} c={c} />
            ))}
          </div>
        )}
      </main>
      <Style />
    </div>
  );
}


function DashboardSummary({ data }: { data: DashboardData }) {
  const { totals, deadlines_soon, pending_settlements } = data;
  const open = totals.by_status?.open || 0;
  const completed = totals.by_status?.completed || 0;
  const settled = totals.by_status?.settled || 0;

  return (
    <section className="mb-8 space-y-5">
      {/* Banda de 4 KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="Recaudado total"
          value={formatEUR(totals.recaudado_total_eur)}
          accent="olive"
        />
        <Kpi
          label="Este mes"
          value={formatEUR(totals.recaudado_mes_eur)}
          accent="brand"
        />
        <Kpi
          label="Por cobrar"
          value={formatEUR(totals.pendiente_cobrar_eur)}
          accent="terra"
        />
        <Kpi
          label="Cobros activos"
          value={`${open} · ${completed + settled} cerrados`}
          accent="ink"
        />
      </div>

      {/* Atención · próximos a vencer o pendientes de liquidar */}
      {(deadlines_soon.length > 0 || pending_settlements.length > 0) && (
        <div className="bg-amberCC-50 border border-[#ECD9AE] rounded-card p-5">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-amberCC-700 mb-3">
            · Atención esta semana
          </div>
          {deadlines_soon.length > 0 && (
            <div className="mb-3">
              <p className="text-[12.5px] text-ink-700 mb-2 font-semibold">
                Cierran en 7 días
              </p>
              <ul className="space-y-1.5">
                {deadlines_soon.slice(0, 5).map((d) => (
                  <li key={d.slug} className="flex items-center justify-between gap-3 text-[13px]">
                    <a
                      href={d.admin_url}
                      className="text-ink hover:text-brand truncate font-display font-semibold"
                    >
                      {d.title}
                    </a>
                    <span className="text-ink-500 whitespace-nowrap text-[12px] font-mono">
                      {d.paid_shares_count}/{d.expected_shares ?? '?'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pending_settlements.length > 0 && (
            <div>
              <p className="text-[12.5px] text-ink-700 mb-2 font-semibold">
                Pendientes de liquidación
              </p>
              <ul className="space-y-1.5">
                {pending_settlements.slice(0, 5).map((p) => (
                  <li key={p.slug} className="flex items-center justify-between gap-3 text-[13px]">
                    <a
                      href={p.admin_url}
                      className="text-ink hover:text-brand truncate font-display font-semibold"
                    >
                      {p.title}
                    </a>
                    <span className="text-ink-500 whitespace-nowrap text-[12px]">
                      {formatEUR(p.total_paid_amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'olive' | 'brand' | 'terra' | 'ink';
}) {
  const color =
    accent === 'olive'
      ? 'text-olive-700'
      : accent === 'brand'
      ? 'text-brand-700'
      : accent === 'terra'
      ? 'text-terra-700'
      : 'text-ink';
  return (
    <div className="bg-paper border border-line rounded-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
        · {label}
      </div>
      <div className={`font-display font-bold text-[20px] tracking-[-0.01em] leading-tight ${color}`}>
        {value}
      </div>
    </div>
  );
}


function CobroCard({ c }: { c: CobroItem }) {
  const copy = copyFor(c.type);
  const s = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
  const progress = c.expected_shares
    ? Math.min(100, (c.paid_shares_count / c.expected_shares) * 100)
    : 0;

  return (
    <article className="bg-paper border border-line shadow-card rounded-card p-6">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · {copy.shortLabel}
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-[11px] font-bold whitespace-nowrap"
          style={{ background: s.bg, color: s.text }}
          title={statusDescription(c.status)}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
          {statusLabel(c.status)}
        </span>
      </div>

      <h2 className="font-display font-bold text-[20px] text-ink tracking-[-0.01em] mb-1 leading-tight">
        {c.title}
      </h2>
      <p className="text-[12.5px] text-ink-500 mb-4">
        Creado el {new Date(c.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
      </p>

      {c.expected_shares ? (
        <>
          <div className="flex justify-between text-[12.5px] text-ink-700 mb-1.5">
            <span>
              {c.paid_shares_count} de {c.expected_shares} {copy.payerPlural}
            </span>
            <span className="font-semibold text-olive-700">
              {formatEUR(c.total_paid_amount)}
            </span>
          </div>
          <div className="h-2 bg-line rounded-pill overflow-hidden mb-5">
            <div
              className="h-full rounded-pill"
              style={{ width: `${progress}%`, background: s.dot }}
            />
          </div>
        </>
      ) : (
        <div className="text-[13.5px] text-olive-700 font-semibold mb-5">
          {formatEUR(c.total_paid_amount)} recaudados
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <a
          href={c.admin_url}
          className="px-4 py-2.5 rounded-xl bg-brand text-paper text-[13px] font-semibold text-center shadow-btn hover:bg-brand-700 transition"
        >
          Panel
        </a>
        <a
          href={c.public_url}
          target="_blank"
          rel="noreferrer"
          className="px-4 py-2.5 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-[13px] font-semibold text-center hover:border-brand transition"
        >
          Ver público
        </a>
      </div>
    </article>
  );
}


function Header() {
  return (
    <header className="border-b border-line bg-paper">
      <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          aria-label="Pagafácil · ir al inicio"
        >
          <Mark />
          <span className="font-display font-bold text-[17px] text-ink tracking-[-0.01em]">
            Pagafácil
          </span>
        </Link>
      </div>
    </header>
  );
}

function Mark({ size = 22 }: { size?: number }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background: 'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
        boxShadow: 'inset 0 0 0 4px #FFFFFF',
      }}
      aria-hidden
    />
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:13px 15px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14.5px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
    `}</style>
  );
}
